import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { deviceAPI, deviceTypeAPI, userAPI, documentAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';

const DeviceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignmentFile, setAssignmentFile] = useState(null);
  const [formData, setFormData] = useState({
    assignedUserId: '',
    deviceTypeId: '',
    name: '',
    brand: '',
    model: '',
    serialNumber: '',
    barcode: '',
    processor: '',
    processorGeneration: '',
    ram: '',
    ramGB: '',
    ramGeneration: '',
    storage: '',
    storageGB: '',
    storageType: '',
    graphicsCard: '',
    operatingSystem: '',
    purchaseDate: '',
    purchasePrice: '',
    warrantyEndDate: '',
    lifespanMonths: '',
    location: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchDeviceTypes();
    fetchUsers();
    if (isEdit) {
      fetchDevice();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll({ isActive: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const fetchDeviceTypes = async () => {
    try {
      const response = await deviceTypeAPI.getAll({ isActive: true });
      setDeviceTypes(response.data);
    } catch (error) {
      toast.error('Cihaz türleri yüklenemedi');
    }
  };

  const fetchDevice = async () => {
    try {
      const response = await deviceAPI.getById(id);
      const device = response.data;
      setFormData({
        assignedUserId: device.assignedUserId || '',
        deviceTypeId: device.deviceTypeId || '',
        name: device.name || '',
        brand: device.brand || '',
        model: device.model || '',
        serialNumber: device.serialNumber || '',
        barcode: device.barcode || '',
        processor: device.processor || '',
        processorGeneration: device.processorGeneration || '',
        ram: device.ram || '',
        ramGB: device.ramGB || '',
        ramGeneration: device.ramGeneration || '',
        storage: device.storage || '',
        storageGB: device.storageGB || '',
        storageType: device.storageType || '',
        graphicsCard: device.graphicsCard || '',
        operatingSystem: device.operatingSystem || '',
        purchaseDate: device.purchaseDate || '',
        purchasePrice: device.purchasePrice || '',
        warrantyEndDate: device.warrantyEndDate || '',
        lifespanMonths: device.lifespanMonths || '',
        location: device.location || '',
        notes: device.notes || '',
        status: device.status || 'active'
      });
    } catch (error) {
      toast.error('Cihaz bilgileri yüklenemedi');
      navigate('/devices');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-fill default lifespan when device type changes
    if (name === 'deviceTypeId') {
      const selectedType = deviceTypes.find(t => t.id === parseInt(value));
      if (selectedType?.defaultLifespan && !formData.lifespanMonths) {
        setFormData(prev => ({ ...prev, [name]: value, lifespanMonths: selectedType.defaultLifespan }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up data
      const data = { ...formData };
      const assignedUserId = data.assignedUserId;
      delete data.assignedUserId; // Remove from device data, handle separately

      Object.keys(data).forEach(key => {
        if (data[key] === '') data[key] = null;
      });

      let deviceId = id;

      if (isEdit) {
        await deviceAPI.update(id, data);
        toast.success('Cihaz güncellendi');
      } else {
        const response = await deviceAPI.create(data);
        deviceId = response.data.id;
        toast.success('Cihaz oluşturuldu');
      }

      // Handle assignment if user selected
      if (assignedUserId) {
        await deviceAPI.assign(deviceId, {
          userId: assignedUserId,
          assignmentDate: new Date().toISOString().split('T')[0],
          description: 'Cihaz formu üzerinden zimmetlendi'
        });

        // Upload document if provided
        if (assignmentFile) {
          const formDataUpload = new FormData();
          formDataUpload.append('document', assignmentFile);
          formDataUpload.append('documentType', 'assignment');
          formDataUpload.append('description', 'Zimmet formu');
          await documentAPI.upload(deviceId, formDataUpload);
          toast.success('Zimmet belgesi yüklendi');
        }
      }

      navigate('/devices');
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  const selectedDeviceType = deviceTypes.find(t => t.id === parseInt(formData.deviceTypeId));
  const isComputer = selectedDeviceType?.name === 'Laptop' || selectedDeviceType?.name === 'Desktop' || selectedDeviceType?.name === 'Sunucu';

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/devices')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Cihaz Düzenle' : 'Yeni Cihaz Ekle'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Info */}
          <div className="lg:col-span-3">
            <h2 className="text-lg font-semibold border-b pb-2 mb-4">Temel Bilgiler</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cihaz Türü *</label>
            <select
              name="deviceTypeId"
              required
              value={formData.deviceTypeId}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Seçiniz</option>
              {deviceTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cihaz Adı *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="örn: Muhasebe-PC-01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Marka</label>
            <input
              type="text"
              name="brand"
              value={formData.brand}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="örn: Dell, HP, Lenovo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Model</label>
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Seri Numarası</label>
            <input
              type="text"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Barkod</label>
            <input
              type="text"
              name="barcode"
              value={formData.barcode}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Assignment Section - Only for new devices */}
          {!isEdit && (
            <>
              <div className="lg:col-span-3">
                <h2 className="text-lg font-semibold border-b pb-2 mb-4 mt-4">Zimmet Bilgileri (İsteğe Bağlı)</h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Zimmetlenecek Kullanıcı</label>
                <select
                  name="assignedUserId"
                  value={formData.assignedUserId}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Stokta (Kimseye zimmetli değil)</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.fullName} - {user.department}</option>
                  ))}
                </select>
              </div>

              {formData.assignedUserId && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Zimmet Belgesi (PDF/Resim)</label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100">
                      <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                      Dosya Seç
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setAssignmentFile(e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                    {assignmentFile && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                        <span className="text-sm text-gray-700">{assignmentFile.name}</span>
                        <button
                          type="button"
                          onClick={() => setAssignmentFile(null)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 10MB) - İsteğe bağlı</p>
                </div>
              )}
            </>
          )}

          {/* Computer Specs - Only show for Laptop/Desktop */}
          {isComputer && (
            <>
              <div className="lg:col-span-3">
                <h2 className="text-lg font-semibold border-b pb-2 mb-4 mt-4">Bilgisayar Özellikleri</h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">İşlemci</label>
                <input
                  type="text"
                  name="processor"
                  value={formData.processor}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: Intel Core i5-12400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">İşlemci Nesli</label>
                <input
                  type="number"
                  name="processorGeneration"
                  value={formData.processorGeneration}
                  onChange={handleChange}
                  min="1"
                  max="20"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: 12 (12. nesil için)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">RAM (GB)</label>
                <input
                  type="number"
                  name="ramGB"
                  value={formData.ramGB}
                  onChange={handleChange}
                  min="1"
                  max="1024"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: 16"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">RAM Nesli (DDR)</label>
                <select
                  name="ramGeneration"
                  value={formData.ramGeneration}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="3">DDR3</option>
                  <option value="4">DDR4</option>
                  <option value="5">DDR5</option>
                  <option value="6">DDR6</option>
                  <option value="7">DDR7</option>
                  <option value="8">DDR8</option>
                  <option value="9">DDR9</option>
                  <option value="10">DDR10</option>
                  <option value="11">DDR11</option>
                  <option value="12">DDR12</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Depolama (GB)</label>
                <input
                  type="number"
                  name="storageGB"
                  value={formData.storageGB}
                  onChange={handleChange}
                  min="1"
                  max="100000"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: 512"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Depolama Tipi</label>
                <select
                  name="storageType"
                  value={formData.storageType}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="HDD">HDD</option>
                  <option value="SSD">SSD</option>
                  <option value="NVMe">NVMe</option>
                  <option value="eMMC">eMMC</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Ekran Kartı</label>
                <input
                  type="text"
                  name="graphicsCard"
                  value={formData.graphicsCard}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: NVIDIA RTX 3060, Intel UHD 630"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">İşletim Sistemi</label>
                <input
                  type="text"
                  name="operatingSystem"
                  value={formData.operatingSystem}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="örn: Windows 11 Pro"
                />
              </div>
            </>
          )}

          {/* Purchase Info */}
          <div className="lg:col-span-3">
            <h2 className="text-lg font-semibold border-b pb-2 mb-4 mt-4">Satın Alma & Ömür</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Alım Tarihi</label>
            <input
              type="date"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Alım Fiyatı (TL)</label>
            <input
              type="number"
              name="purchasePrice"
              value={formData.purchasePrice}
              onChange={handleChange}
              step="0.01"
              min="0"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Garanti Bitiş Tarihi</label>
            <input
              type="date"
              name="warrantyEndDate"
              value={formData.warrantyEndDate}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Beklenen Ömür (Ay)</label>
            <input
              type="number"
              name="lifespanMonths"
              value={formData.lifespanMonths}
              onChange={handleChange}
              min="1"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="örn: 48"
            />
            {selectedDeviceType?.defaultLifespan && (
              <p className="text-xs text-gray-500 mt-1">Varsayılan: {selectedDeviceType.defaultLifespan} ay</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Lokasyon</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="örn: Merkez Ofis, Kat 2"
            />
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Durum</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
                <option value="maintenance">Bakımda</option>
              </select>
            </div>
          )}

          {/* Notes */}
          <div className="lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700">Notlar</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/devices')}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Kaydet')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeviceForm;
